import React, { useState } from 'react';
import { Song, Playlist } from '../../types';

interface MusicPanelProps {
  songs: Song[];
  playlists: Playlist[];
  currentSongId: string | null;
  isPlaying: boolean;
  isPaused: boolean;
  onPlaySong: (songId: string, options?: { context?: 'library' | 'playlist' | 'queue'; playlistSongs?: Song[]; shuffle?: boolean }) => void;
  onStopSong: () => void;
  onGoToLibraryManager: () => void;
  onAddToQueue: (songId: string) => void;
  onClearQueue: () => void;
  onShuffleChange: (enabled: boolean) => void;
}

export const MusicPanel: React.FC<MusicPanelProps> = ({
  songs,
  playlists,
  currentSongId,
  isPlaying,
  isPaused,
  onPlaySong,
  onStopSong,
  onGoToLibraryManager,
  onAddToQueue,
  onClearQueue,
  onShuffleChange
}) => {
  const [isPlaylistsExpanded, setIsPlaylistsExpanded] = useState(false);
  const [isAllSongsExpanded, setIsAllSongsExpanded] = useState(false);
  const [playlistSearch, setPlaylistSearch] = useState('');
  const [songSearch, setSongSearch] = useState('');

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Play all songs in a playlist
  const handlePlayPlaylist = (playlistId: string) => {
    const playlistSongs = songs.filter(s => playlists.find(p => p.id === playlistId)?.songIds.includes(s.id));
    if (playlistSongs.length === 0) return;
    
    onShuffleChange(false); // Update UI state
    onPlaySong(playlistSongs[0].id, { context: 'playlist', playlistSongs, shuffle: false });
  };

  // Shuffle and play all songs in a playlist
  const handleShufflePlaylist = (playlistId: string) => {
    const playlistSongs = songs.filter(s => playlists.find(p => p.id === playlistId)?.songIds.includes(s.id));
    if (playlistSongs.length === 0) return;
    
    onShuffleChange(true); // Update UI state
    onPlaySong(playlistSongs[0].id, { context: 'playlist', playlistSongs, shuffle: true });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with Library Manager Button */}
      <div className="p-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Quick Play</h3>
        <button
          onClick={onGoToLibraryManager}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded transition-colors"
          title="Open Library Manager"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Manage
        </button>
      </div>

      {/* Songs List */}
      <div className="flex-1">
        {songs.length === 0 ? (
          <div className="p-4 text-center">
            <svg className="h-12 w-12 mx-auto mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            <p className="text-xs text-gray-500 mb-2">No songs yet</p>
            <button
              onClick={onGoToLibraryManager}
              className="text-xs text-purple-600 hover:text-purple-700 underline"
            >
              Upload songs
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {/* Playlists Section */}
            {playlists.length > 0 && (
              <div className="p-3">
                <button
                  onClick={() => setIsPlaylistsExpanded(!isPlaylistsExpanded)}
                  className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 hover:text-gray-700 transition-colors"
                >
                  <span>Playlists</span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{
                      transform: isPlaylistsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                
                {isPlaylistsExpanded && (
                  <>
                    <div className="relative mb-2">
                      <input
                        type="text"
                        placeholder="Search playlists..."
                        value={playlistSearch}
                        onChange={(e) => setPlaylistSearch(e.target.value)}
                        className="w-full px-2 py-1.5 pr-7 text-xs rounded text-gray-900 placeholder-gray-500 bg-white border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:border-gray-600"
                      />
                      {playlistSearch && (
                        <button
                          onClick={() => setPlaylistSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Clear search"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="space-y-1">
                      {playlists
                        .filter(playlist => 
                          playlist.name.toLowerCase().includes(playlistSearch.toLowerCase())
                        )
                        .map(playlist => {
                          const playlistSongs = songs.filter(s => playlist.songIds.includes(s.id));
                          if (playlistSongs.length === 0) return null;
                          
                          return (
                            <div key={playlist.id} className="group relative">
                              <div className="text-xs font-medium text-gray-700 px-2 py-1.5 hover:bg-gray-100 rounded flex items-center gap-2">
                                <svg className="h-3.5 w-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                                <span className="flex-1 truncate">{playlist.name}</span>
                                {/* Song count - hidden on hover */}
                                <span className="text-gray-400 group-hover:opacity-0 transition-opacity">
                                  {playlistSongs.length}
                                </span>
                              </div>
                              {/* Action buttons on hover */}
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-100 rounded">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePlayPlaylist(playlist.id);
                                  }}
                                  className="p-1 rounded hover:bg-purple-200 transition-colors"
                                  title="Play playlist"
                                >
                                  <svg className="h-3 w-3 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShufflePlaylist(playlist.id);
                                  }}
                                  className="p-1 rounded hover:bg-purple-200 transition-colors"
                                  title="Shuffle playlist"
                                >
                                  <svg className="h-3 w-3 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      {playlists.filter(playlist => 
                        playlist.name.toLowerCase().includes(playlistSearch.toLowerCase())
                      ).length === 0 && (
                        <div className="text-xs text-gray-400 italic px-2 py-1">
                          No playlists found
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* All Songs Section */}
            <div className="p-3">
              <button
                onClick={() => setIsAllSongsExpanded(!isAllSongsExpanded)}
                className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 hover:text-gray-700 transition-colors"
              >
                <span>All Songs</span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: isAllSongsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              
              {isAllSongsExpanded && (
                <>
                  <div className="relative mb-2">
                    <input
                      type="text"
                      placeholder="Search songs..."
                      value={songSearch}
                      onChange={(e) => setSongSearch(e.target.value)}
                      className="w-full px-2 py-1.5 pr-7 text-xs rounded text-gray-900 placeholder-gray-500 bg-white border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:border-gray-600"
                    />
                    {songSearch && (
                      <button
                        onClick={() => setSongSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Clear search"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                      </button>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {songs
                      .filter(song => 
                        song.name.toLowerCase().includes(songSearch.toLowerCase())
                      )
                      .slice(0, 10)
                      .map(song => {
                        const isCurrent = song.id === currentSongId;
                        const isActivelyPlaying = isCurrent && isPlaying && !isPaused;
                        return (
                          <div
                            key={song.id}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded transition-colors ${
                              isCurrent ? 'bg-purple-50' : 'hover:bg-gray-100'
                            }`}
                          >
                            <button
                              onClick={() => isActivelyPlaying ? onStopSong() : onPlaySong(song.id, { context: 'library' })}
                              className="flex-shrink-0"
                            >
                              {isActivelyPlaying ? (
                                <svg className="h-4 w-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="h-4 w-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-gray-900 truncate font-medium">{song.name}</div>
                              <div className="text-xs text-gray-600 truncate">{song.artist?.trim() || 'Unknown Artist'}</div>
                              <div className="text-xs text-gray-500">{formatDuration(song.duration)}</div>
                            </div>
                            {isActivelyPlaying && (
                              <div className="flex gap-0.5">
                                <div className="w-0.5 h-2.5 bg-purple-600 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-0.5 h-2.5 bg-purple-600 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-0.5 h-2.5 bg-purple-600 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {songs.filter(song => 
                      song.name.toLowerCase().includes(songSearch.toLowerCase())
                    ).length === 0 && (
                      <div className="text-xs text-gray-400 italic px-2 py-1">
                        No songs found
                      </div>
                    )}
                    {songs.filter(song => 
                      song.name.toLowerCase().includes(songSearch.toLowerCase())
                    ).length > 10 && (
                      <button
                        onClick={onGoToLibraryManager}
                        className="w-full text-xs text-purple-600 hover:text-purple-700 text-left px-2 py-1.5"
                      >
                        View all {songs.filter(song => 
                          song.name.toLowerCase().includes(songSearch.toLowerCase())
                        ).length} songs →
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
